import { motion } from 'framer-motion';
import { Sparkles, BarChart3, Target } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';

export const HowItWorks = () => {
  return (
    <section className="relative py-24 sm:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent dark:via-primary/[0.06]" />
      <div className="container relative mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          {/* Left Side - Text Content (styling only; no change to right-side chat) */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <span className="text-primary font-semibold text-sm">FIVE</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-[1.1] tracking-tight">
              <span className="text-foreground">AI Career Copilot </span>
              <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                That Understands You
              </span>
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Analyzes your unique requirements, career trajectory, and designs
              personalized micro-actions that compound into massive professional
              growth.
            </p>
          </motion.div>

          {/* Right Side - Chat Example */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <Card className="p-8 bg-gradient-to-br from-background to-secondary/20 border-2 border-border/50 rounded-3xl shadow-2xl">
              {/* AI Agent Icon */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                className="flex items-center gap-3 mb-6"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="relative"
                >
                  <Logo
                    variant="icon"
                    className="w-12 h-12 rounded-xl"
                  />
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 bg-primary/20 rounded-xl blur-md -z-10"
                  />
                </motion.div>
                <div>
                  <h3 className="font-bold text-foreground">Five AI</h3>
                  <p className="text-xs text-muted-foreground">
                    Your Career Strategist
                  </p>
                </div>
              </motion.div>

              {/* Chat Messages */}
              <div className="space-y-4 mb-6">
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, type: 'spring', stiffness: 100 }}
                  className="bg-primary/90 text-primary-foreground p-6 rounded-3xl rounded-tl-md shadow-lg animate-fade-in relative overflow-hidden"
                >
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                    className="absolute bottom-0 left-0 h-1 bg-primary-foreground/20"
                  />
                  <p className="text-base leading-relaxed">
                    Hi Sarah! I've analyzed your profile as a Senior Marketing
                    Manager. Based on your goals, let's start with a 5-minute
                    action that will increase your visibility to leadership.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6, type: 'spring', stiffness: 100 }}
                  className="bg-primary/90 text-primary-foreground p-6 rounded-3xl rounded-tl-md shadow-lg animate-fade-in relative overflow-hidden"
                >
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.7, duration: 0.8 }}
                    className="absolute bottom-0 left-0 h-1 bg-primary-foreground/20"
                  />
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8, duration: 0.3 }}
                  >
                    <p className="text-base leading-relaxed">
                      Today's action: Schedule a 15-minute coffee chat with your
                      VP to discuss the Q3 campaign insights you mentioned.
                      Here's the exact email template...
                    </p>
                  </motion.div>
                </motion.div>
              </div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
                className="flex flex-wrap gap-3 mb-6"
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full bg-background/80 hover:bg-background border-border/50"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate email
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full bg-background/80 hover:bg-background border-border/50"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Track progress
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full bg-background/80 hover:bg-background border-border/50"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Next action
                  </Button>
                </motion.div>
              </motion.div>

              {/* Footer Text */}
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 1 }}
                className="text-center text-sm text-muted-foreground"
              >
                Personalized guidance, not generic advice
              </motion.p>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

